import { useEffect, useState } from 'react';
import { useTranslator } from 'tradera/lang/translate';
import { Button } from 'tradera/components/atoms/button/button';
import { Popover } from 'tradera/components/popover/popover';
import ShareIcon from 'tradera/blueprint/theme/icons2/share.svg?jsx';
import ShareSantaIcon from './share-santa.svg?jsx';
import {
    hideShareWishlistTooltip,
    shouldShowShareWishlistSantaTooltip,
    shouldShowShareWishlistTooltip,
    hideShareWishlistSantaTooltip,
} from './helper';
import { GtmService } from 'tradera/services/google-tagmanager/google-tagmanager-service';
import { GA_EVENT_CATEGORY_WISHLIST_SHARE } from 'tradera/apps/tradera/components/wish-list-share/wish-list-share';
import type { ButtonSize } from 'tradera/components/atoms/button/button';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';

export type WishListShareButtonProps = {
    showText?: boolean;
    showTooltip?: boolean;
    size?: ButtonSize;
    gaEventLabel: string;
    onClick: () => void;
};

export const WishListShareButton = ({
    showText,
    showTooltip,
    size = 'md',
    gaEventLabel,
    onClick,
}: WishListShareButtonProps) => {
    const { t } = useTranslator();

    const wishlistSantaEnabled = useIsFeatureEnabled('wish-list-share-santa');

    const [shouldShowTooltip, setShouldShowTooltip] = useState(false);
    useEffect(() => {
        setShouldShowTooltip(
            !!(
                showTooltip &&
                (wishlistSantaEnabled
                    ? shouldShowShareWishlistSantaTooltip()
                    : shouldShowShareWishlistTooltip())
            ),
        );
    }, [showTooltip, wishlistSantaEnabled]);

    const handleTooltipClose = () => {
        if (wishlistSantaEnabled) {
            hideShareWishlistSantaTooltip();
        } else {
            hideShareWishlistTooltip();
        }
        setShouldShowTooltip(false);
    };

    const handleTooltipClick = () => {
        GtmService.trackAction(
            GA_EVENT_CATEGORY_WISHLIST_SHARE,
            'Open share - tooltip',
            gaEventLabel,
        );
        onClick();
        handleTooltipClose();
    };

    const handleClick = () => {
        onClick();
        GtmService.trackAction(
            GA_EVENT_CATEGORY_WISHLIST_SHARE,
            'Open share',
            gaEventLabel,
        );
    };

    return (
        <Popover
            title={t('shareModal_wishlist_tooltip_title')}
            text={t('shareModal_wishlist_tooltip_text')}
            buttonText={t('shareModal_wishlist_tooltip_button')}
            onClick={handleTooltipClick}
            onClose={handleTooltipClose}
            defaultOpen={shouldShowTooltip}>
            <Button
                theme="text"
                icon={
                    wishlistSantaEnabled ? (
                        <ShareSantaIcon className="icon icon-md" />
                    ) : (
                        <ShareIcon className="icon icon-md" />
                    )
                }
                size={size}
                round={!showText}
                className="px-2 px-md-0"
                title={t('myt_wishList_share')}
                onClick={handleClick}>
                {showText && <span>{t('myt_wishList_share')}</span>}
            </Button>
        </Popover>
    );
};
